import { useState, useEffect } from "react"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { ACCESS_TOKEN } from "@pma/utils/constants"
import { FINANCIAL_SERVER_URL, SET_PROJECT_SIGNATURE_ENDPOINT } from "@pma/utils/api-routes"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import { Label } from "@components/common/label"
import { Separator } from "@components/common/separator"
import { Button } from "@components/common/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"

interface Props {
  projectId: string
  signaturesRequirement?: number
}

const ProjectSignatureSettings = ({ projectId, signaturesRequirement = 0 }: Props) => {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()

  const [signForBills, setSignForBills] = useState(signaturesRequirement)

  useEffect(() => {
    setSignForBills(signaturesRequirement ?? 0)
  }, [signaturesRequirement])

  const updateMutation = useMutation({
    mutationKey: ["update-project-signatures", projectId],
    mutationFn: async () => {
      const { data } = await axios.post(
        FINANCIAL_SERVER_URL + SET_PROJECT_SIGNATURE_ENDPOINT,
        { sign_for_bills: signForBills, project_id: Number(projectId) },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )
      return data
    },
    onSuccess: () => {
      toast({ title: translate("Success"), description: translate("Signatures updated"), variant: "success" })
      queryClient.invalidateQueries({ queryKey: ["get-project", projectId] })
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to update signatures"),
        variant: "error",
      })
    },
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{translate("Signature Requirements")}</CardTitle>
        <CardDescription>{translate("Configure the signature requirements for this project")}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-6'>
        <Separator />
        <div className='grid gap-2'>
          <Label>{translate("Number of bill signatures Required")}</Label>
          <div className='flex flex-row gap-2 lg:w-1/3'>
            <Select
              value={String(signForBills)}
              onValueChange={(value) => setSignForBills(Number(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='0'>0 ({translate("Auto-approve")})</SelectItem>
                <SelectItem value='1'>{translate("1 signature")}</SelectItem>
                <SelectItem value='2'>{translate("2 signatures")}</SelectItem>
                <SelectItem value='3'>{translate("3 signatures")}</SelectItem>
                <SelectItem value='4'>{translate("4 signatures")}</SelectItem>
                <SelectItem value='5'>{translate("5 signatures")}</SelectItem>
                <SelectItem value='6'>{translate("6 signatures")}</SelectItem>
              </SelectContent>
            </Select>
            {signForBills !== signaturesRequirement && (
              <Button
                size='sm'
                className='w-[100px]'
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending}
              >
                {translate("Save changes")}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default ProjectSignatureSettings
